import { CardGroup } from 'API';
import type { SearchSpxTransactionsInput, SpxTransactionsOrConditionFilterInput } from 'CDK_API';
import { SEARCH_SORT_DIRECTION, SearchSortDirection } from 'app/util/searchTypes';
import { addMonths, format } from 'date-fns';
import { SpxTransactionsPage, transactionService } from 'app/services/transaction.service';
import { cardService } from './card.service';
import { Dispatch } from '@reduxjs/toolkit';

export interface GetGroupTransactionsArgs {
  paidolId: string;
  // Optional because exactly one of them applies per call: getGroupTransactions
  // reads cardGroupId and getTransactionsByProjectIds reads projectGroupId, and
  // GroupTransactions hands the same object to whichever branch its groupType
  // selects. Requiring both made the shape tests supply an id the function under
  // test ignores, which only obscured what they pin.
  cardGroupId?: string;
  projectGroupId?: string;
  nextToken?: string;
  filterDate?: Date | null;
  searchTerms?: string | null;
  sortField?: string;
  sortOrder?: SearchSortDirection;
}

interface GetGroupArgs {
  groupId: string;
  nextToken?: string;
  cardLimit?: number;
}

export const getGroupTransactions = async ({
  paidolId,
  cardGroupId,
  nextToken,
  filterDate,
  searchTerms,
  sortField,
  sortOrder = SEARCH_SORT_DIRECTION.desc,
}: GetGroupTransactionsArgs) => {
  const and: Array<SpxTransactionsOrConditionFilterInput> = [
    {
      or: [
        {
          cardGroupId: { eq: cardGroupId },
        },
      ],
    },
  ];

  // SEE-301: a SEPARATE `and` entry, not another member of `and[0].or`. Pushing the
  // merchant clauses in beside `cardGroupId` produced
  //   paidolId = X AND (cardGroupId = G OR merchantName ~ *t* OR merchantCategory ~ *t*)
  // which ORs the group scope away and would list matching transactions from every
  // other group in the paidol. As its own entry the clauses AND with the group,
  // matching the shape getJobCodeTransactions uses.
  //
  // Real in the code, not reachable through the UI: GroupTransactions is the only
  // caller and its ActionBar search block is commented out, so `searchTerms` is
  // always null. This is correctness for when that box returns, not a live defect.
  if (searchTerms) {
    and.push({
      or: [
        { merchantName: { wildcard: `*${searchTerms}*` } },
        { merchantCategory: { wildcard: `*${searchTerms}*` } },
      ],
    });
  }

  const variables: { input: SearchSpxTransactionsInput } = {
    input: {
      filter: {
        paidolId: { eq: paidolId },
        ...(filterDate && {
          eventDate: {
            gt: format(new Date(filterDate), 'yyyy-MM'),
            lt: format(addMonths(new Date(filterDate), 1), 'yyyy-MM'),
          },
        }),
        and,
      },
      ...(sortField && {
        sort: {
          field: sortField,
          direction: sortOrder,
        },
      }),
      nextToken,
      limit: 20,
    },
  };

  return transactionService.searchSpxTransactions(variables.input) as Promise<SpxTransactionsPage | null>;
};

export const getTransactionsByProjectIds = async ({
  paidolId,
  projectGroupId,
  filterDate,
  nextToken,
  searchTerms,
  sortField,
  sortOrder = SEARCH_SORT_DIRECTION.desc,
}: GetGroupTransactionsArgs) => {
  // Combine projectGroupId and cardsOrProjects into one object
  const and: Array<SpxTransactionsOrConditionFilterInput> = [
    {
      or: [
        {
          projectGroupId: { eq: projectGroupId },
        },
      ],
    },
  ];

  // SEE-301: searchTerms was destructured away, so this branch would ignore a term
  // the card-group branch honours — GroupTransactions passes it for both group types,
  // so the two diverge the moment search is switched back on. Same shape as
  // getGroupTransactions: its own `and` entry, so the clauses AND with the
  // project-group scope instead of ORing it away.
  //
  // Also not reachable today, for the same reason as above: that caller's search box
  // is commented out and `searchTerms` is always null. Parity for when it returns.
  if (searchTerms) {
    and.push({
      or: [
        { merchantName: { wildcard: `*${searchTerms}*` } },
        { merchantCategory: { wildcard: `*${searchTerms}*` } },
      ],
    });
  }

  const variables: { input: SearchSpxTransactionsInput } = {
    input: {
      filter: {
        paidolId: { eq: paidolId },
        ...(filterDate && {
          eventDate: {
            gt: format(new Date(filterDate), 'yyyy-MM'),
            lt: format(addMonths(new Date(filterDate), 1), 'yyyy-MM'),
          },
        }),
        and,
      },
      ...(sortField && {
        sort: {
          field: sortField,
          direction: sortOrder,
        },
      }),
      nextToken,
      limit: 20,
    },
  };

  return transactionService.searchSpxTransactions(variables.input) as Promise<SpxTransactionsPage | null>;
};

// SEE-1232 row b: read from CDK AppSync, re-keyed to the Amplify CardGroup shape in
// cardService. `dispatch` is kept so the call sites are unchanged.
export const getCardGroup = async ({ groupId, cardLimit }: GetGroupArgs, _dispatch?: Dispatch) => {
  return cardService.getCardGroup<CardGroup>({
    id: groupId,
    ...(cardLimit && { cardLimit }),
  });
};

/**
 * SEE-301: served by the CDK AppSync API.
 *
 * `group_id` is snake_case — the live wire contract on both APIs, not a typo.
 *
 * Throws on failure rather than returning `undefined`. The CDK resolver enforces
 * `assert_paidol_member`, and a denial arrives as a generic GraphQL error that
 * cannot be told apart from a real backend failure; swallowing it here would
 * render an empty project group as if the data legitimately did not exist.
 * `undefined` is still returned for the genuine not-found case.
 */
export const getProjectGroupData = async ({ groupId }: GetGroupArgs) => {
  const payload = await transactionService.v2GetProjectGroup({ group_id: groupId });

  if (!payload) {
    console.warn('No data returned from getProjectGroup query.');
    return undefined;
  }

  const projectGroup = payload.projectGroup ?? undefined;
  const projects = payload.projects ?? undefined;
  const groupAdmins = payload.groupAdmins ?? undefined;

  if (!projectGroup) {
    console.warn('Project group not found or is undefined.');
  }

  return { projectGroup, projects, groupAdmins };
};

export const addCardsToGroup = async (paidolId: string, cardGroupId: string, cardIds: string[]) => {
  return cardService.addCardsToCardGroup({
    input: {
      cardGroupId,
      paidolId,
      cardsIds: cardIds,
    },
  });
};
